// src/context/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../api/axios";
import { connectSocket, disconnectSocket } from "./socket";
import { setOnAuthFail } from "../api/authEvents";
import { linkCookieConsentToCustomer } from "../utils/linkCookieConsent";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState(null);
  const [username, setUsername] = useState(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [tierlevel, setTierlevel] = useState(null);
  const [creditlimit, setCreditlimit] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [socketConnected, setSocketConnected] = useState(false);

  const hardLogoutLocal = () => {
    disconnectSocket();
    setSocketConnected(false);

    setIsAuthenticated(false);
    setUserId(null);
    setUsername(null);
    setImgUrl(null);
    setTierlevel(null);
    setBalance(null);
    setCreditlimit(null);

    sessionStorage.clear();
  };

  // ✅ Register global auth-fail handler (used by axios interceptor)
  useEffect(() => {
    setOnAuthFail(() => {
      hardLogoutLocal();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthStatus = async () => {
    try {
      const res = await api.get("/cauthRoutes/customer/status");
      const {
        isAuthenticated,
        userId,
        username,
        imgUrl,
        tierlevel,
        balance,
        creditlimit,
      } = res.data;

      setIsAuthenticated(isAuthenticated);
      setUserId(userId);
      setUsername(username);
      setImgUrl(imgUrl);
      setTierlevel(tierlevel);
      setBalance(balance);
      setCreditlimit(creditlimit);

      // 🔌 Connect socket only if logged in
      if (isAuthenticated && !socketConnected) {
        await connectSocket();
        setSocketConnected(true);
      }
      if (isAuthenticated) {
        await linkCookieConsentToCustomer();
      }

      if (!isAuthenticated && socketConnected) {
        disconnectSocket();
        setSocketConnected(false);
      }
    } catch (err) {
      hardLogoutLocal();
    } finally {
      setLoading(false);
    }
  };

  const login = async (authData) => {
    setIsAuthenticated(true);
    setUserId(authData.userId);
    setUsername(authData.username);
    setImgUrl(authData.imgUrl);
    setTierlevel(authData.tierlevel);
    setBalance(authData.balance);
    setCreditlimit(authData.creditlimit);
    await linkCookieConsentToCustomer();

    connectSocket()
      .then(() => setSocketConnected(true))
      .catch(() => setSocketConnected(false));
  };

  const logout = async () => {
    try {
      await api.post("/cauthRoutes/customer/logout", { userId });
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      hardLogoutLocal();
    }
  };

  // Initial auth check
  useEffect(() => {
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-check on tab focus (debounced)
  useEffect(() => {
    let t;

    const run = () => {
      clearTimeout(t);
      t = setTimeout(() => checkAuthStatus(), 300);
    };

    window.addEventListener("focus", run);

    const onVis = () => {
      if (document.visibilityState === "visible") run();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      window.removeEventListener("focus", run);
      document.removeEventListener("visibilitychange", onVis);
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userId,
        username,
        imgUrl,
        tierlevel,
        balance,
        creditlimit,
        loading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);